import type { Project } from '@shared/models/project.model';
import type { Results } from '@shared/models/unit.model';
import type { ElementPath } from '@shared/helpers/recursive-element.helpers';
import { getElementCategoryId } from '@shared/helpers/element-category-id.helpers';
import { countStoreysAboveGroundFromDesign } from '@shared/helpers/boverket-climate-declaration/boverket-climate-declaration-building.helpers';
import { getBuildingDesignFromProjectOrBuilding } from '@shared/helpers/design/design.helpers';
import {
  C1MaterialGroup,
  C1_CRANE_DIESEL_KWH_PER_TON,
  C1_DIESEL_BASE_KWH_PER_M2,
  C1_ELECTRICITY_KWH_PER_M2,
  C1_MATERIAL_DIESEL_KWH_PER_TON,
  C1_MATERIAL_GROUP_BY_CATEGORY,
  C_PHASE_GWP_DIESEL_PER_KWH,
  C_PHASE_GWP_ELECTRICITY_PER_KWH,
} from './c-phase.constants';
import type { CPhaseMapContext } from './c-phase.model';

const KG_PER_TON = 1000;

/**
 * Resolve the C1 material group from the owning material category in the path.
 * Walks ancestors nearest-first and returns the first recognised material group,
 * defaulting to `'other'` (steel/other share the same factors).
 */
export const getC1MaterialGroup = (path: ElementPath): C1MaterialGroup => {
  for (let i = path.length - 1; i >= 0; i--) {
    const group = C1_MATERIAL_GROUP_BY_CATEGORY[getElementCategoryId(path[i])];
    if (group) return group;
  }
  return 'other';
};

/**
 * Crane diesel scaling for tall buildings (IVL S11):
 * `max(floors_above_ground - 2, 0) / floors_above_ground`. Returns 0 when the
 * building has no floors above ground (no crane lift needed).
 */
export const getC1CraneFactor = (project: Project): number => {
  const floorsAboveGround = countStoreysAboveGroundFromDesign(
    getBuildingDesignFromProjectOrBuilding(project),
  );
  if (floorsAboveGround <= 0) return 0;
  return Math.max(floorsAboveGround - 2, 0) / floorsAboveGround;
};

/**
 * C1 demolition emissions from a single product's material mass (IVL S11, DEV-3323).
 *
 * Per-product part only: demolition diesel from material weight plus the tall-building
 * crane add-on, both per ton of `results.kg`. The GFA-based base energy (electricity +
 * base diesel) is building-wide and is emitted once via {@link getC1BaseEnergyResults}.
 * Coverage (täckningsgrad) is applied later by the rollup, so it is not divided here.
 */
export const mapResultsByC1 = (
  results: Results,
  context: CPhaseMapContext,
): Results => {
  const mass = results.kg ?? 0;
  if (!mass) return results;

  const group = getC1MaterialGroup(context.path);
  const massTon = mass / KG_PER_TON;
  const craneFactor = getC1CraneFactor(context.project);
  const dieselKwh =
    massTon *
    (C1_MATERIAL_DIESEL_KWH_PER_TON[group] +
      craneFactor * C1_CRANE_DIESEL_KWH_PER_TON[group]);
  const co2e = dieselKwh * C_PHASE_GWP_DIESEL_PER_KWH;
  if (!co2e) return results;

  return { ...results, co2e_C1: (results.co2e_C1 ?? 0) + co2e };
};

/**
 * Building-wide C1 base energy (IVL S11, DEV-3323): demolition site electricity and
 * base diesel scaled by gross floor area (BTA). Emitted once at the version level,
 * mirroring how B7 operational water is added, so it is not multiplied per product.
 *
 * NOTE on coverage (täckningsgrad): DEV-3323 step 6 divides the whole `C1_BAU` by
 * `coverage_rate`. We intentionally do NOT upscale this GFA-based term — GFA already
 * represents the full building, so dividing by coverage would over-count it. Only the
 * per-product material/crane diesel ({@link mapResultsByC1}) is upscaled, via the normal
 * rollup. This matches how the C3-C4 BD7/BD8 schablon is treated. Keep it this way.
 */
export const getC1BaseEnergyResults = (gfa: number): Results => {
  if (!gfa) return {};
  const electricityCo2e =
    gfa * C1_ELECTRICITY_KWH_PER_M2 * C_PHASE_GWP_ELECTRICITY_PER_KWH;
  const dieselCo2e =
    gfa * C1_DIESEL_BASE_KWH_PER_M2 * C_PHASE_GWP_DIESEL_PER_KWH;
  const co2e = electricityCo2e + dieselCo2e;
  return co2e ? { co2e_C1: co2e } : {};
};
